import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Archive,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  CircleDashed,
  Clock,
  Crown,
  Eye,
  FileText,
  FolderKanban,
  Hammer,
  Layers,
  Mail,
  Plus,
  Settings,
  ShieldCheck,
  Tag,
  Trash2,
  User,
  UserPlus,
  UsersRound,
} from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { EmptyState } from '@/components/common/empty-state';
import type { ProjectLayoutContext } from '@/components/layout/project-layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { extractApiErrorMessage } from '@/lib/api';
import { listMembers } from '@/lib/org-api';
import {
  addProjectMember,
  createStatus,
  deleteProject,
  deleteStatus,
  getProjectByKey,
  listProjectMembers,
  listStatuses,
  removeProjectMember,
  reorderStatuses,
  updateProject,
  updateStatus,
  type IssueStatusRow,
  type ProjectMemberRow,
  type StatusCategory,
} from '@/lib/project-api';
import { useAuthStore } from '@/stores/auth-store';
import { cn } from '@/lib/utils';

type Tab = 'general' | 'members' | 'statuses';

const TABS: Array<{ id: Tab; label: string; icon: typeof Settings }> = [
  { id: 'general', label: 'General', icon: Settings },
  { id: 'members', label: 'Members', icon: UsersRound },
  { id: 'statuses', label: 'Statuses', icon: FolderKanban },
];

const CATEGORY_META: Record<
  StatusCategory,
  {
    label: string;
    icon: typeof CircleDashed;
    iconColor: string;
    chipBg: string;
    pill: string;
  }
> = {
  TODO: {
    label: 'To do',
    icon: CircleDashed,
    iconColor: 'text-slate-500 dark:text-slate-400',
    chipBg: 'bg-slate-500/10 ring-slate-500/20',
    pill: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  },
  IN_PROGRESS: {
    label: 'In progress',
    icon: Clock,
    iconColor: 'text-sky-600 dark:text-sky-400',
    chipBg: 'bg-sky-500/10 ring-sky-500/20',
    pill: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  },
  DONE: {
    label: 'Done',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    chipBg: 'bg-emerald-500/10 ring-emerald-500/20',
    pill: 'bg-emerald-500/10 text-emerald-700 ring-emerald-500/30 dark:text-emerald-300',
  },
};

const ROLE_META: Record<
  'LEAD' | 'MEMBER' | 'VIEWER',
  { label: string; icon: typeof Crown; color: string; pill: string }
> = {
  LEAD: {
    label: 'Lead',
    icon: Crown,
    color: 'text-amber-500',
    pill: 'bg-amber-500/10 text-amber-700 ring-amber-500/30 dark:text-amber-300',
  },
  MEMBER: {
    label: 'Member',
    icon: User,
    color: 'text-sky-500',
    pill: 'bg-sky-500/10 text-sky-700 ring-sky-500/30 dark:text-sky-300',
  },
  VIEWER: {
    label: 'Viewer',
    icon: Eye,
    color: 'text-slate-500',
    pill: 'bg-slate-500/10 text-slate-700 ring-slate-500/30 dark:text-slate-300',
  },
};

export function ProjectSettingsPage() {
  const { orgId, orgSlug, projectKey } = useOutletContext<ProjectLayoutContext>();
  const [tab, setTab] = useState<Tab>('general');
  const user = useAuthStore((s) => s.user);
  const membership = user?.memberships.find((m) => m.organizationId === orgId);
  const canEdit = Boolean(user?.isSuperAdmin || membership?.permissions.includes('project.edit'));
  const canDelete = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('project.delete'),
  );
  const canStatusAdmin = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('status.manage_custom'),
  );
  const canManageMembers = Boolean(
    user?.isSuperAdmin || membership?.permissions.includes('project.add_members'),
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-5 p-6">
      {/* Hero header */}
      <div className="relative overflow-hidden rounded-xl border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent">
        <div
          className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-primary/10 blur-3xl"
          aria-hidden
        />
        <div className="relative flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20">
              <Settings className="h-5 w-5" aria-hidden />
            </div>
            <div className="min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">Project settings</h1>
              <p className="mt-1 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                <span>Profile, membership, and status workflow for</span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background/80 px-2 py-0.5 font-mono text-xs font-medium ring-1 ring-border">
                  {projectKey}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Tabs row */}
        <div className="flex flex-wrap items-center gap-1 border-t bg-muted/30 px-3 py-2">
          {TABS.map((t) => {
            const Icon = t.icon;
            const selected = tab === t.id;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                aria-pressed={selected}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                  selected
                    ? 'bg-background text-foreground shadow-sm ring-1 ring-border'
                    : 'text-muted-foreground hover:bg-background/60 hover:text-foreground',
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'general' ? (
        <GeneralTab
          orgId={orgId}
          orgSlug={orgSlug}
          projectKey={projectKey}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      ) : tab === 'members' ? (
        <MembersTab orgId={orgId} projectKey={projectKey} canEdit={canManageMembers} />
      ) : (
        <StatusesTab orgId={orgId} projectKey={projectKey} canEdit={canStatusAdmin} />
      )}
    </div>
  );
}

// -------------------- General --------------------

function GeneralTab({
  orgId,
  orgSlug,
  projectKey,
  canEdit,
  canDelete,
}: {
  orgId: string;
  orgSlug: string;
  projectKey: string;
  canEdit: boolean;
  canDelete: boolean;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<'archive' | 'unarchive' | 'delete' | null>(
    null,
  );

  useEffect(() => {
    if (projectQuery.data) {
      setName(projectQuery.data.name);
      setDescription(projectQuery.data.description ?? '');
    }
  }, [projectQuery.data]);

  const patch = useMutation({
    mutationFn: (body: Parameters<typeof updateProject>[2]) =>
      updateProject(orgId, projectQuery.data!.id, body),
    onSuccess: (p) => {
      qc.setQueryData(['org', orgId, 'project', projectKey], p);
      qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] });
      success('Project updated');
    },
    onError: (e) => error('Could not save', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: () => deleteProject(orgId, projectQuery.data!.id),
    onSuccess: () => {
      success('Project deleted');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'projects'] });
      navigate(`/o/${orgSlug}/projects`);
    },
    onError: (e) => error('Delete failed', extractApiErrorMessage(e)),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!projectQuery.data || patch.isPending) return;
    patch.mutate({
      name: name.trim(),
      description: description.trim() === '' ? null : description.trim(),
    });
  };

  if (projectQuery.isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }
  if (projectQuery.isError || !projectQuery.data) {
    return (
      <EmptyState
        title="Could not load project"
        description={extractApiErrorMessage(projectQuery.error)}
      />
    );
  }

  const project = projectQuery.data;
  const archived = Boolean(project.archivedAt);

  return (
    <div className="space-y-5">
      <Card className="overflow-hidden">
        <div className="h-1 w-full bg-gradient-to-r from-primary/60 via-primary/30 to-transparent" />
        <div className="flex items-start gap-3 border-b bg-muted/30 px-4 py-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-primary/20">
            <FileText className="h-4 w-4" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold">Project profile</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Key <span className="font-mono font-medium text-foreground">{project.key}</span> is
              immutable. Archiving hides it from default lists.
            </p>
          </div>
          {archived ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-500/30 dark:text-amber-300">
              <Archive className="h-3 w-3" aria-hidden /> Archived
            </span>
          ) : null}
        </div>
        <CardContent className="space-y-5 p-5">
          <form className="space-y-5" onSubmit={submit}>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label
                  htmlFor="pset-name"
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  <Tag className="h-3.5 w-3.5" aria-hidden /> Name
                </Label>
                <Input
                  id="pset-name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!canEdit}
                  className="h-11 text-sm"
                />
              </div>
              <div className="space-y-2">
                <Label
                  htmlFor="pset-key"
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Key
                  <span className="ml-1 font-normal normal-case text-muted-foreground/80">
                    (immutable)
                  </span>
                </Label>
                <Input
                  id="pset-key"
                  value={project.key}
                  disabled
                  readOnly
                  className="h-11 font-mono text-sm"
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label
                  htmlFor="pset-desc"
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                >
                  <FileText className="h-3.5 w-3.5" aria-hidden /> Description
                </Label>
                <div className="relative rounded-lg border focus-within:ring-2 focus-within:ring-ring">
                  <textarea
                    id="pset-desc"
                    className="min-h-[110px] w-full resize-y rounded-lg bg-background p-3 pr-16 text-sm focus:outline-none disabled:opacity-60"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    disabled={!canEdit}
                    maxLength={1000}
                    placeholder="What is this project about?"
                  />
                  <span className="pointer-events-none absolute bottom-2 right-3 text-[10px] tabular-nums text-muted-foreground">
                    {description.length}/1000
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button type="submit" disabled={!canEdit || patch.isPending} className="gap-1.5">
                {patch.isPending ? (
                  <>
                    <CircleDashed className="h-4 w-4 animate-spin" aria-hidden /> Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4" aria-hidden /> Save changes
                  </>
                )}
              </Button>
              {archived ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canEdit}
                  onClick={() => setConfirmRemove('unarchive')}
                  className="gap-1.5"
                >
                  <Archive className="h-4 w-4" aria-hidden /> Unarchive
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  disabled={!canEdit}
                  onClick={() => setConfirmRemove('archive')}
                  className="gap-1.5"
                >
                  <Archive className="h-4 w-4" aria-hidden /> Archive
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Danger zone */}
      {canDelete ? (
        <Card className="overflow-hidden border-rose-500/30">
          <div className="h-1 w-full bg-gradient-to-r from-rose-500 via-rose-400/60 to-transparent" />
          <div className="flex items-start gap-3 border-b border-rose-500/20 bg-rose-500/5 px-4 py-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-rose-500/10 text-rose-600 ring-1 ring-rose-500/30 dark:text-rose-400">
              <AlertTriangle className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm font-semibold">Danger zone</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Permanently destroys issues, sprints, statuses, and attachments. There is no undo.
              </p>
            </div>
          </div>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 p-4">
            <div className="text-sm">
              <div className="font-medium">Delete this project</div>
              <div className="text-xs text-muted-foreground">
                Only org admins and super admins can perform this action.
              </div>
            </div>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setConfirmRemove('delete')}
              className="gap-1.5"
            >
              <Trash2 className="h-4 w-4" aria-hidden /> Delete project
            </Button>
          </CardContent>
        </Card>
      ) : null}

      <ConfirmDialog
        open={confirmRemove === 'archive'}
        title="Archive this project?"
        description="Archived projects are hidden from the default list and can be restored later."
        confirmLabel="Archive"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          await patch.mutateAsync({ archived: true });
          setConfirmRemove(null);
        }}
      />
      <ConfirmDialog
        open={confirmRemove === 'unarchive'}
        title="Restore this project?"
        confirmLabel="Unarchive"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          await patch.mutateAsync({ archived: false });
          setConfirmRemove(null);
        }}
      />
      <ConfirmDialog
        open={confirmRemove === 'delete'}
        destructive
        title={`Permanently delete ${project.key}?`}
        description="This deletes all issues, sprints, statuses, and attachments — there is no undo."
        confirmLabel="Delete forever"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          await remove.mutateAsync();
          setConfirmRemove(null);
        }}
      />
    </div>
  );
}

// -------------------- Members --------------------

function MembersTab({
  orgId,
  projectKey,
  canEdit,
}: {
  orgId: string;
  projectKey: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });
  const membersQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'members'],
    queryFn: () =>
      projectQuery.data ? listProjectMembers(orgId, projectQuery.data.id) : Promise.resolve([]),
    enabled: Boolean(projectQuery.data),
  });
  const orgMembersQuery = useQuery({
    queryKey: ['org', orgId, 'members', 'all'],
    queryFn: () => listMembers(orgId, { limit: 100 }),
  });

  const [inviteUserId, setInviteUserId] = useState('');
  const [inviteRole, setInviteRole] = useState<'LEAD' | 'MEMBER' | 'VIEWER'>('MEMBER');
  const [confirmRemove, setConfirmRemove] = useState<ProjectMemberRow | null>(null);

  const add = useMutation({
    mutationFn: () =>
      addProjectMember(orgId, projectQuery.data!.id, {
        userId: inviteUserId,
        role: inviteRole,
      }),
    onSuccess: () => {
      success('Member added');
      setInviteUserId('');
      setInviteRole('MEMBER');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'members'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey] });
    },
    onError: (e) => error('Add failed', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => removeProjectMember(orgId, projectQuery.data!.id, userId),
    onSuccess: () => {
      success('Member removed');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'members'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey] });
      setConfirmRemove(null);
    },
    onError: (e) => error('Remove failed', extractApiErrorMessage(e)),
  });

  const projectMemberIds = new Set((membersQuery.data ?? []).map((m) => m.userId));
  const eligible = (orgMembersQuery.data?.items ?? []).filter(
    (m) => !projectMemberIds.has(m.userId),
  );

  const members = membersQuery.data ?? [];
  const counts = {
    total: members.length,
    leads: members.filter((m) => m.role === 'LEAD').length,
    viewers: members.filter((m) => m.role === 'VIEWER').length,
  };

  return (
    <Card className="overflow-hidden">
      <div className="h-1 w-full bg-gradient-to-r from-sky-500/60 via-sky-400/30 to-transparent" />
      <div className="flex items-start gap-3 border-b bg-muted/30 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-500/10 text-sky-600 ring-1 ring-sky-500/20 dark:text-sky-400">
          <UsersRound className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Members</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Leads can edit settings and manage members. Viewers have read-only access.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 ring-1 ring-border">
            <UsersRound className="h-3 w-3" aria-hidden /> {counts.total} total
          </span>
          <span className="hidden items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-amber-700 ring-1 ring-amber-500/30 sm:inline-flex dark:text-amber-300">
            <Crown className="h-3 w-3" aria-hidden /> {counts.leads} leads
          </span>
        </div>
      </div>

      <CardContent className="space-y-4 p-4">
        {canEdit ? (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (!inviteUserId || add.isPending) return;
              add.mutate();
            }}
          >
            <div>
              <Label
                htmlFor="pm-user"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <UserPlus className="h-3 w-3" aria-hidden /> Add an org member
              </Label>
              <Select
                id="pm-user"
                value={inviteUserId}
                onChange={(e) => setInviteUserId(e.target.value)}
                required
              >
                <option value="">Choose a teammate…</option>
                {eligible.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name} ({m.email})
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label
                htmlFor="pm-role"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Crown className="h-3 w-3" aria-hidden /> Role
              </Label>
              <Select
                id="pm-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as typeof inviteRole)}
                wrapperClassName="w-40"
              >
                <option value="LEAD">Lead</option>
                <option value="MEMBER">Member</option>
                <option value="VIEWER">Viewer</option>
              </Select>
            </div>
            <Button
              type="submit"
              disabled={!inviteUserId || add.isPending}
              className="gap-1.5 self-end"
            >
              <Plus className="h-4 w-4" aria-hidden /> Add member
            </Button>
          </form>
        ) : null}

        {membersQuery.isError ? (
          <EmptyState
            title="Could not load members"
            description={extractApiErrorMessage(membersQuery.error)}
          />
        ) : members.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 py-10 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background ring-1 ring-border">
              <UsersRound className="h-4 w-4 text-muted-foreground" aria-hidden />
            </div>
            <p className="text-sm font-medium">No project members yet</p>
            <p className="text-xs text-muted-foreground">
              {canEdit
                ? 'Add teammates to collaborate on this project.'
                : 'Ask a lead to add you.'}
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {members.map((m) => {
              const roleMeta = ROLE_META[m.role];
              const RoleIcon = roleMeta.icon;
              const initial = m.name.trim().charAt(0).toUpperCase() || '?';
              return (
                <li
                  key={m.userId}
                  className="group flex flex-wrap items-center gap-3 rounded-lg border bg-background px-3 py-2.5 shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold uppercase text-primary ring-1 ring-primary/20">
                    {initial}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-semibold">{m.name}</span>
                      <span
                        className={cn(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                          roleMeta.pill,
                        )}
                      >
                        <RoleIcon className="h-3 w-3" aria-hidden />
                        {roleMeta.label}
                      </span>
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      <Mail className="h-3 w-3" aria-hidden />
                      <span className="truncate font-mono">{m.email}</span>
                      <span className="mx-1">·</span>
                      <span className="text-[11px]">
                        joined {new Date(m.joinedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {canEdit ? (
                    <div className="flex items-center gap-2">
                      <Select
                        value={m.role}
                        onChange={(e) =>
                          addProjectMember(orgId, projectQuery.data!.id, {
                            userId: m.userId,
                            role: e.target.value as 'LEAD' | 'MEMBER' | 'VIEWER',
                          })
                            .then(() => {
                              qc.invalidateQueries({
                                queryKey: ['org', orgId, 'project', projectKey, 'members'],
                              });
                            })
                            .catch((err) => error('Update failed', extractApiErrorMessage(err)))
                        }
                        wrapperClassName="w-36"
                        className="text-xs"
                      >
                        <option value="LEAD">Lead</option>
                        <option value="MEMBER">Member</option>
                        <option value="VIEWER">Viewer</option>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmRemove(m)}
                        className="gap-1 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
                        title="Remove from project"
                      >
                        <Trash2 className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <ConfirmDialog
        open={Boolean(confirmRemove)}
        destructive
        title={`Remove ${confirmRemove?.name ?? ''} from project?`}
        description="They will lose access to issues, sprints, and settings for this project. Their org membership is untouched."
        confirmLabel="Remove"
        onCancel={() => setConfirmRemove(null)}
        onConfirm={async () => {
          if (!confirmRemove) return;
          await remove.mutateAsync(confirmRemove.userId);
        }}
      />
    </Card>
  );
}

// -------------------- Statuses --------------------

function StatusesTab({
  orgId,
  projectKey,
  canEdit,
}: {
  orgId: string;
  projectKey: string;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const projectQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey],
    queryFn: () => getProjectByKey(orgId, projectKey),
  });
  const statusesQuery = useQuery({
    queryKey: ['org', orgId, 'project', projectKey, 'statuses'],
    queryFn: () =>
      projectQuery.data ? listStatuses(orgId, projectQuery.data.id) : Promise.resolve([]),
    enabled: Boolean(projectQuery.data),
  });

  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState<StatusCategory>('TODO');
  const [confirmDelete, setConfirmDelete] = useState<IssueStatusRow | null>(null);
  const [replacementId, setReplacementId] = useState('');

  const add = useMutation({
    mutationFn: () =>
      createStatus(orgId, projectQuery.data!.id, {
        name: newName.trim(),
        category: newCategory,
      }),
    onSuccess: () => {
      success('Status created');
      setNewName('');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey] });
    },
    onError: (e) => error('Could not create status', extractApiErrorMessage(e)),
  });

  const patch = useMutation({
    mutationFn: (args: { id: string; body: Parameters<typeof updateStatus>[3] }) =>
      updateStatus(orgId, projectQuery.data!.id, args.id, args.body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
    },
    onError: (e) => error('Could not update status', extractApiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (args: { id: string; replacement: string }) =>
      deleteStatus(orgId, projectQuery.data!.id, args.id, args.replacement),
    onSuccess: () => {
      success('Status deleted', 'Issues reassigned to the replacement');
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'issues'] });
      setConfirmDelete(null);
    },
    onError: (e) => error('Delete failed', extractApiErrorMessage(e)),
  });

  const reorder = useMutation({
    mutationFn: (orderedIds: string[]) => reorderStatuses(orgId, projectQuery.data!.id, orderedIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org', orgId, 'project', projectKey, 'statuses'] });
    },
    onError: (e) => error('Reorder failed', extractApiErrorMessage(e)),
  });

  const handleMove = (status: IssueStatusRow, direction: -1 | 1) => {
    const ordered = (statusesQuery.data ?? []).slice().sort((a, b) => a.order - b.order);
    const idx = ordered.findIndex((s) => s.id === status.id);
    const targetIdx = idx + direction;
    if (idx < 0 || targetIdx < 0 || targetIdx >= ordered.length) return;
    const next = ordered.slice();
    const [removed] = next.splice(idx, 1);
    if (!removed) return;
    next.splice(targetIdx, 0, removed);
    reorder.mutate(next.map((s) => s.id));
  };

  const statuses = (statusesQuery.data ?? []).slice().sort((a, b) => a.order - b.order);
  const replacementOptions = confirmDelete ? statuses.filter((s) => s.id !== confirmDelete.id) : [];

  return (
    <Card className="overflow-hidden">
      <div className="h-1 w-full bg-gradient-to-r from-emerald-500/60 via-emerald-400/30 to-transparent" />
      <div className="flex items-start gap-3 border-b bg-muted/30 px-4 py-3">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 ring-1 ring-emerald-500/20 dark:text-emerald-400">
          <FolderKanban className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Statuses</h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Configure the project's status workflow. Each category must keep at least one status.
            Deleting a status requires a replacement so its issues don&apos;t lose state.
          </p>
        </div>
      </div>

      <CardContent className="space-y-4 p-4">
        {canEdit ? (
          <form
            className="grid gap-3 sm:grid-cols-[1fr_auto_auto] sm:items-end"
            onSubmit={(e) => {
              e.preventDefault();
              if (!newName.trim() || add.isPending) return;
              add.mutate();
            }}
          >
            <div>
              <Label
                htmlFor="st-name"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Tag className="h-3 w-3" aria-hidden /> Status name
              </Label>
              <Input
                id="st-name"
                required
                maxLength={40}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="In Review"
                className="h-10 text-sm"
              />
            </div>
            <div>
              <Label
                htmlFor="st-cat"
                className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground"
              >
                <Hammer className="h-3 w-3" aria-hidden /> Category
              </Label>
              <Select
                id="st-cat"
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value as StatusCategory)}
                wrapperClassName="w-44"
              >
                <option value="TODO">To do</option>
                <option value="IN_PROGRESS">In progress</option>
                <option value="DONE">Done</option>
              </Select>
            </div>
            <Button type="submit" disabled={!newName.trim() || add.isPending} className="gap-1.5">
              <Plus className="h-4 w-4" aria-hidden /> Add status
            </Button>
          </form>
        ) : null}

        {statusesQuery.isError ? (
          <EmptyState
            title="Could not load statuses"
            description={extractApiErrorMessage(statusesQuery.error)}
          />
        ) : statuses.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 py-10 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background ring-1 ring-border">
              <FolderKanban className="h-4 w-4 text-muted-foreground" aria-hidden />
            </div>
            <p className="text-sm font-medium">No statuses yet</p>
            <p className="text-xs text-muted-foreground">
              Add a starting status for this project.
            </p>
          </div>
        ) : (
          <ul className="space-y-2">
            {statuses.map((s, idx) => {
              const catMeta = CATEGORY_META[s.category];
              const CatIcon = catMeta.icon;
              const isFirst = idx === 0;
              const isLast = idx === statuses.length - 1;
              return (
                <li
                  key={s.id}
                  className="group flex flex-wrap items-center gap-3 overflow-hidden rounded-lg border bg-background shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
                >
                  {/* Left color rail using the status color */}
                  <span
                    className="h-full w-1 self-stretch"
                    style={{ background: s.colorHex }}
                    aria-hidden
                  />
                  <span
                    className="tabular-nums rounded bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground ring-1 ring-border"
                    title={`Order ${s.order}`}
                  >
                    #{idx + 1}
                  </span>
                  <div
                    className={cn(
                      'flex h-8 w-8 items-center justify-center rounded-lg ring-1',
                      catMeta.chipBg,
                    )}
                    title={catMeta.label}
                  >
                    <CatIcon className={cn('h-4 w-4', catMeta.iconColor)} aria-hidden />
                  </div>

                  {canEdit ? (
                    <Input
                      defaultValue={s.name}
                      className="h-9 w-48 text-sm"
                      onBlur={(e) => {
                        const next = e.target.value.trim();
                        if (next && next !== s.name) {
                          patch.mutate({ id: s.id, body: { name: next } });
                        }
                      }}
                    />
                  ) : (
                    <span className="text-sm font-semibold">{s.name}</span>
                  )}

                  {canEdit ? (
                    <Select
                      value={s.category}
                      wrapperClassName="w-40"
                      className="text-xs"
                      onChange={(e) =>
                        patch.mutate({
                          id: s.id,
                          body: { category: e.target.value as StatusCategory },
                        })
                      }
                    >
                      <option value="TODO">To do</option>
                      <option value="IN_PROGRESS">In progress</option>
                      <option value="DONE">Done</option>
                    </Select>
                  ) : (
                    <span
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1',
                        catMeta.pill,
                      )}
                    >
                      <CatIcon className="h-3 w-3" aria-hidden />
                      {catMeta.label}
                    </span>
                  )}

                  <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold tabular-nums ring-1 ring-border">
                    <Layers className="h-3 w-3 text-muted-foreground" aria-hidden />
                    {s.issueCount}
                  </span>

                  <div className="ml-auto flex items-center gap-1 pr-3">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canEdit || reorder.isPending || isFirst}
                      onClick={() => handleMove(s, -1)}
                      className="h-8 w-8 p-0"
                      title="Move up"
                      aria-label={`Move ${s.name} up`}
                    >
                      <ArrowUp className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canEdit || reorder.isPending || isLast}
                      onClick={() => handleMove(s, 1)}
                      className="h-8 w-8 p-0"
                      title="Move down"
                      aria-label={`Move ${s.name} down`}
                    >
                      <ArrowDown className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canEdit}
                      onClick={() => {
                        setConfirmDelete(s);
                        const first = statuses.find((st) => st.id !== s.id);
                        setReplacementId(first?.id ?? '');
                      }}
                      className="h-8 w-8 p-0 text-rose-600 hover:bg-rose-500/10 hover:text-rose-700"
                      title="Delete status"
                      aria-label={`Delete ${s.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        destructive
        title={`Delete status "${confirmDelete?.name ?? ''}"?`}
        description={
          replacementOptions.length === 0
            ? 'No other statuses exist to receive issues — add one before deleting.'
            : `All ${confirmDelete?.issueCount ?? 0} issues currently in this status will be moved to the replacement.`
        }
        confirmLabel={replacementOptions.length === 0 ? 'Nothing to delete to' : 'Delete status'}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (!confirmDelete || !replacementId) return;
          await remove.mutateAsync({
            id: confirmDelete.id,
            replacement: replacementId,
          });
        }}
      />
      {confirmDelete && replacementOptions.length > 0 ? (
        <div className="border-t bg-muted/30 px-4 py-3">
          <Label className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <FolderKanban className="h-3 w-3" aria-hidden /> Replacement status
          </Label>
          <Select
            value={replacementId}
            onChange={(e) => setReplacementId(e.target.value)}
            wrapperClassName="max-w-sm"
          >
            {replacementOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({CATEGORY_META[s.category].label})
              </option>
            ))}
          </Select>
        </div>
      ) : null}
    </Card>
  );
}
